import { Edit3, History } from 'lucide-react';
import { Button } from '../../ui/button';
import type { StockPlanningItem, ColumnPreference } from '../../../lib/api';

interface StockPlanningTableProps {
  items: StockPlanningItem[];
  columns: ColumnPreference[];
  /**
   * When supplied, an extra Actions column renders with a "Propose change"
   * button per row. Used by the supplier-side grid; left undefined for the
   * customer view so no column is shown.
   */
  onProposeChange?: (item: StockPlanningItem) => void;
  /**
   * Optional. When supplied alongside onProposeChange, a "History" button
   * is rendered next to "Propose" so the supplier can open the part's
   * change-request history without leaving the grid.
   */
  onViewHistory?: (item: StockPlanningItem) => void;
}

/** Columns that should be frozen (sticky) on the left when scrolling horizontally */
const FROZEN_COLUMNS = ['part_number', 'description'];

export default function StockPlanningTable({ items, columns, onProposeChange, onViewHistory }: StockPlanningTableProps) {
  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No stock planning data available.
      </div>
    );
  }

  const getCellValue = (item: StockPlanningItem, columnId: string): string => {
    const value = item[columnId as keyof StockPlanningItem];
    if (value === undefined || value === null) return '-';
    // Remaining-to-produce blanks when zero (per Mark: "that'll go blank... it's a complete top")
    if (columnId === 'remaining_to_produce') {
      const num = parseFloat(String(value));
      if (!isNaN(num) && num <= 0) return '';
    }
    return String(value);
  };

  const renderFulfilmentBar = (raw: string) => {
    const pct = parseFloat(raw);
    if (isNaN(pct)) return <span className="text-muted-foreground">-</span>;
    const clamped = Math.max(0, Math.min(100, pct));
    const barColor = pct >= 100 ? 'bg-emerald-500' : pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-2 min-w-[120px]">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${clamped}%` }} />
        </div>
        <span className="text-xs font-medium tabular-nums whitespace-nowrap">{pct.toFixed(0)}%</span>
      </div>
    );
  };

  const getCellClassName = (columnId: string, value: string): string => {
    if (columnId === 'directs_covered') {
      return value === 'YES' ? 'text-green-600 font-medium' : 'text-red-600 font-medium';
    }
    if (columnId === 'current_shortage' || columnId === 'stock_health_min') {
      const num = parseFloat(value);
      if (!isNaN(num) && num < 0) return 'text-red-600';
      if (!isNaN(num) && num > 0) return 'text-green-600';
    }
    return '';
  };

  // Calculate left offsets for frozen columns
  const FROZEN_WIDTHS: Record<string, number> = {
    part_number: 130,
    description: 160,
  };

  const getFrozenLeft = (columnId: string): number => {
    let left = 0;
    for (const frozenId of FROZEN_COLUMNS) {
      if (frozenId === columnId) break;
      // Only accumulate if this frozen column is actually visible
      if (columns.some(c => c.column_id === frozenId)) {
        left += FROZEN_WIDTHS[frozenId] ?? 120;
      }
    }
    return left;
  };

  const isFrozen = (columnId: string): boolean => FROZEN_COLUMNS.includes(columnId);

  return (
    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-340px)] border rounded-lg">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-20">
          <tr className="border-b bg-muted/80 backdrop-blur-sm">
            {columns.map((col) => {
              const frozen = isFrozen(col.column_id);
              return (
                <th
                  key={col.column_id}
                  className={`whitespace-normal break-words align-top text-xs text-left font-medium px-3 py-2 ${
                    frozen ? 'sticky z-30 bg-muted shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''
                  }`}
                  style={frozen ? {
                    left: getFrozenLeft(col.column_id),
                    minWidth: FROZEN_WIDTHS[col.column_id] ?? 120,
                  } : { minWidth: 100 }}
                >
                  {col.label}
                </th>
              );
            })}
            {onProposeChange && (
              <th className="text-xs text-right font-medium px-3 py-2" style={{ minWidth: 130 }}>
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-b hover:bg-muted/50">
              {columns.map((col) => {
                const rawValue = item[col.column_id as keyof StockPlanningItem];
                const value = getCellValue(item, col.column_id);
                const frozen = isFrozen(col.column_id);
                return (
                  <td
                    key={col.column_id}
                    className={`text-sm px-3 py-2 align-top whitespace-normal break-words ${getCellClassName(col.column_id, value)} ${
                      frozen ? 'sticky z-10 bg-background shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''
                    }`}
                    style={frozen ? {
                      left: getFrozenLeft(col.column_id),
                      minWidth: FROZEN_WIDTHS[col.column_id] ?? 120,
                      maxWidth: FROZEN_WIDTHS[col.column_id] ?? 120,
                    } : undefined}
                  >
                    {col.column_id === 'fulfilment_pct'
                      ? renderFulfilmentBar(String(rawValue ?? ''))
                      : value}
                  </td>
                );
              })}
              {onProposeChange && (
                <td className="text-sm px-3 py-2 align-top text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onProposeChange(item)}
                      title="Propose qty change for this part"
                    >
                      <Edit3 className="h-4 w-4 mr-1.5" />
                      Propose
                    </Button>
                    {onViewHistory && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewHistory(item)}
                        title="View part change history"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
